import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarRows,
  Card,
  ChartCard,
  Eyebrow,
  Spinner,
  TrendChart,
  formatDate,
  formatTime,
  money,
} from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { StatusBadge } from '../components/DataTable.js';
import { useResource } from '../lib/useResource.js';
import { useLocale, useT } from '../i18n/index.js';

/**
 * The dispatcher's board (docs/06 "/"). The signature element is the departure
 * strip: today's services as a single scannable band, coloured by operational
 * status, which is the first question anyone running a bus company asks.
 */

interface Dashboard {
  date: string;
  timezone: string;
  currency: string;
  departures: Array<{
    id: string;
    departureAt: string;
    status: string;
    delayMinutes: number;
    routeName: string;
    busName: string;
    seatsBooked: number;
    capacity: number;
  }>;
  today: { bookings: number; revenueCents: number; seats: number };
  week: { bookings: number; revenueCents: number };
  openDisputes: number;
  unpaidBookings: number;
}

interface TrendMonth {
  month: string;
  bookings: number;
  seats: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
  commissionCents: number;
}

interface RoutePerformanceRow {
  routeId: string;
  routeName: string;
  seatsOffered: number;
  seatsSold: number;
  loadFactor: number;
  bookings: number;
  gmvCents: number;
}

function Stat({
  label,
  value,
  hint,
  href,
}: {
  label: string;
  value: string;
  hint?: string;
  href?: string;
}) {
  const inner = (
    <Card className="h-full">
      <Eyebrow>{label}</Eyebrow>
      <p className="mt-1.5 text-2xl font-semibold tabular tracking-tight">{value}</p>
      {hint && (
        <p className="mt-0.5 text-xs" style={{ color: 'var(--color-muted)' }}>
          {hint}
        </p>
      )}
    </Card>
  );
  return href ? (
    <Link to={href} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

export function Dashboard() {
  const t = useT();
  const { locale } = useLocale();
  const [months, setMonths] = useState(12);
  const board = useResource<Dashboard>('/admin/dashboard');
  const trends = useResource<{ months: number; series: TrendMonth[] }>(
    `/admin/trends?months=${months}`,
  );
  const routes = useResource<{ routes: RoutePerformanceRow[] }>(
    '/admin/route-performance?days=365&window=departed',
  );

  if (board.loading) {
    return (
      <Page title={t('nav.dashboard')}>
        <div className="grid place-items-center py-16">
          <Spinner label={t('common.loading')} />
        </div>
      </Page>
    );
  }
  if (board.error || !board.data) {
    return (
      <Page title={t('nav.dashboard')}>
        <p style={{ color: 'var(--signal-cancelled)' }}>{board.error}</p>
      </Page>
    );
  }

  const data = board.data;
  const tz = data.timezone;

  return (
    <Page
      title={t('nav.dashboard')}
      description={t('db.allTimesIn', { date: formatDate(`${data.date}T12:00:00Z`, tz), tz })}
    >
      <section>
        <Eyebrow>{t('db.todaysDepartures')}</Eyebrow>
        {data.departures.length === 0 ? (
          <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
            {t('db.nothingToday')}
          </p>
        ) : (
          <ol className="mt-2 flex gap-2 overflow-x-auto pb-2">
            {data.departures.map((departure) => {
              const full = departure.capacity > 0 && departure.seatsBooked >= departure.capacity;
              return (
                <li key={departure.id} className="shrink-0">
                  <Link
                    to="/trips"
                    className="block w-44 rounded-xl border p-3"
                    style={{ borderColor: 'var(--color-line)', background: 'var(--color-bg)' }}
                  >
                    <span className="board-time text-lg">
                      {formatTime(departure.departureAt, tz)}
                    </span>
                    <p className="mt-1 truncate text-xs font-medium">{departure.routeName}</p>
                    <p className="truncate text-xs" style={{ color: 'var(--color-faint)' }}>
                      {departure.busName}
                    </p>
                    <p className="mt-1.5 text-xs tabular">
                      <span style={{ color: full ? 'var(--signal-delayed)' : 'var(--color-text)' }}>
                        {departure.seatsBooked}
                      </span>
                      <span style={{ color: 'var(--color-faint)' }}> / {departure.capacity}</span>
                    </p>
                    <div className="mt-1.5">
                      <StatusBadge status={departure.status} />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label={t('db.bookingsToday')}
          value={String(data.today.bookings)}
          hint={t('db.seatsSold', { count: data.today.seats })}
          href="/bookings"
        />
        <Stat
          label={t('db.revenueToday')}
          value={money(data.today.revenueCents, data.currency)}
          hint={t('db.netRefunds')}
        />
        <Stat
          label={t('db.revenueWeek')}
          value={money(data.week.revenueCents, data.currency)}
          hint={t('db.bookingsCount', { count: data.week.bookings })}
        />
        <Stat
          label={t('db.openDisputes')}
          value={String(data.openDisputes)}
          hint={data.openDisputes > 0 ? t('db.disputesWaiting') : t('db.disputesNone')}
          href="/disputes"
        />
      </section>

      {/* No eyebrow here: each chart card carries its own title, and repeating
          "Bookings by month" directly above a card of the same name is noise. */}
      <div className="mt-7 flex items-center justify-end gap-3">
        <div className="flex gap-1">
          {[12, 24].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setMonths(option)}
              className="rounded-md border px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{
                borderColor: months === option ? 'var(--color-line-strong)' : 'var(--color-line)',
                color: months === option ? 'var(--color-text)' : 'var(--color-faint)',
              }}
              aria-pressed={months === option}
            >
              {option === 12 ? t('db.window12') : t('db.window24')}
            </button>
          ))}
        </div>
      </div>

      {trends.data && (
        <div className="mt-3 grid gap-3 xl:grid-cols-2">
          <ChartCard title={t('db.trendTitle')} hint={t('db.trendHint', { months })}>
            <TrendChart
              points={trends.data.series.map((m) => ({ month: m.month, value: m.bookings }))}
              format={(value) => value.toLocaleString()}
              label={t('db.trendSeries')}
              locale={locale}
            />
          </ChartCard>
          <ChartCard
            title={t('db.revenueTrendTitle')}
            hint={t('db.revenueTrendHint', { currency: data.currency })}
          >
            <TrendChart
              points={trends.data.series.map((m) => ({ month: m.month, value: m.netCents }))}
              format={(value) => money(value, data.currency)}
              label={t('db.revenueSeries')}
              locale={locale}
            />
          </ChartCard>
        </div>
      )}

      {routes.data && (
        <div className="mt-3">
          <ChartCard title={t('db.routesTitle')} hint={t('db.routesHint')}>
            <BarRows
              emptyLabel={t('db.noActivity')}
              rows={routes.data.routes.map((route) => ({
                key: route.routeId,
                label: route.routeName,
                // Load factor, not revenue: revenue alone cannot tell a route that
                // is full and cheap from one that is empty and expensive, and those
                // two need opposite decisions.
                value: route.loadFactor,
                hint: t('db.loadFactor', { percent: Math.round(route.loadFactor * 100) }),
                caption: `${route.seatsSold.toLocaleString()} / ${route.seatsOffered.toLocaleString()} · ${money(
                  route.gmvCents,
                  data.currency,
                )}`,
              }))}
            />
          </ChartCard>
        </div>
      )}

      {data.unpaidBookings > 0 && (
        <p className="mt-4 text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('db.awaitingPayment', { count: data.unpaidBookings })}
        </p>
      )}
    </Page>
  );
}
